import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import has from 'lodash/has';
import { TListHighlightBlockData } from '../../Highlight/types';

const getDepthOfFirstMeaningfulChild = (list: TListHighlightBlockData): number => {
  let depth = 0;
  let el = list;
  while (el?.items?.[0]) {
    // eslint-disable-next-line prefer-destructuring
    el = el.items[0];
    // eslint-disable-next-line no-plusplus
    depth++;
  }
  return depth;
};

const itemsFiller = (list: TListHighlightBlockData, depth: number): TListHighlightBlockData =>
  depth > 0 ? itemsFiller({ content: '', items: [list] }, depth - 1) : list;

const withFirstMeaningfulChildOnTheSameLevel = (
  list: TListHighlightBlockData,
  rawListFragment: TListHighlightBlockData,
): TListHighlightBlockData =>
  itemsFiller(rawListFragment, getDepthOfFirstMeaningfulChild(list) - getDepthOfFirstMeaningfulChild(rawListFragment));

// TODO matchMode
const isListMatching = (list: TListHighlightBlockData, rawListFragment: TListHighlightBlockData): boolean => {
  const listFragment = withFirstMeaningfulChildOnTheSameLevel(list, rawListFragment);

  if ((list?.content || '') !== (listFragment?.content || '')) {
    return false;
  }
  if (listFragment?.items?.length > list?.items?.length) {
    return false;
  }
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < listFragment?.items?.length; i++) {
    if (!isListMatching(list?.items[i], listFragment?.items[i])) {
      return false;
    }
  }

  return true;
};

const getListAndPathWithoutFirstNestedChild = (
  list: TListHighlightBlockData,
  inOriginalPath: string,
): { list: TListHighlightBlockData; path: string } => {
  const result = cloneDeep(list);
  let nestedListToModify = result;
  let pathParts = inOriginalPath.split('.').map((el) => (isNaN(+el) ? el : +el));

  let currentDepth = 1;
  while (nestedListToModify?.items?.[0] || nestedListToModify?.content) {
    if (nestedListToModify?.content) {
      delete nestedListToModify.content;

      return { list: result, path: pathParts.join('.') };
    }

    if (nestedListToModify.items[0]?.items?.[0]) {
      nestedListToModify = nestedListToModify?.items?.[0];
      // eslint-disable-next-line no-plusplus
      currentDepth++;
      if (!has(pathParts, currentDepth)) {
        pathParts[currentDepth] = 0;
      }
    } else {
      nestedListToModify.items = nestedListToModify.items.slice(1);
      pathParts = pathParts.slice(0, currentDepth + 1);
      // eslint-disable-next-line no-plusplus
      pathParts[currentDepth]++;

      return { list: result, path: pathParts.join('.') };
    }
  }

  return { list: result, path: pathParts.join('.') };
};

export const findListFragmentPositionInList = (
  list: TListHighlightBlockData,
  listFragment: TListHighlightBlockData,
): string | undefined => {
  let el = list;
  let inOriginalPath = 'root.0';
  do {
    if (isListMatching(el, listFragment)) {
      return inOriginalPath;
    }
    ({ list: el, path: inOriginalPath } = getListAndPathWithoutFirstNestedChild(el, inOriginalPath));
  } while (el?.content || !isEmpty(el?.items));

  return undefined;
};
